import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import * as searchApi from '@/features/search/api';
import { buildSearchRequest, parseQuery } from '@/features/search/query';
import SparkQLInput from '@/features/search/sparkql-input';
import { useAppData } from '@/app-data';
import { useIdentity } from '@/features/identity/identity';
import { X } from '@/components/ui/icons';

/**
 * "Add search results" picker (SBB-14): type a SparkQL query — with the same field/value autocomplete as
 * the global search — preview the live match count, and drop a search-results table onto the canvas.
 */
export default function SearchQueryPicker({
  open,
  onClose,
  onPick,
  initialQuery = '',
  title = 'Add search results',
  confirmLabel = 'Add to canvas',
}: {
  open: boolean;
  onClose: () => void;
  onPick: (query: string) => void;
  initialQuery?: string;
  title?: string;
  confirmLabel?: string;
}) {
  const { boards, members } = useAppData();
  const { me } = useIdentity();
  const [q, setQ] = useState('');
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (open) { setQ(initialQuery); setCount(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const s = ++seq.current;
    setLoading(true);
    const t = window.setTimeout(() => {
      const req = buildSearchRequest(parseQuery(q), { boards, members, meId: me?.id }, 1);
      searchApi
        .searchItems(req)
        .then((r) => { if (s === seq.current) setCount(r.total); })
        .catch(() => { if (s === seq.current) setCount(null); })
        .finally(() => { if (s === seq.current) setLoading(false); });
    }, 220);
    return () => window.clearTimeout(t);
  }, [q, open, boards, members, me?.id]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  const add = () => { onPick(q.trim()); onClose(); };

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-start justify-center bg-black/40 pt-[14vh]" onPointerDown={onClose}>
      <div className="w-[480px] max-w-[92vw] rounded-2xl border border-line bg-surface-raised shadow-2xl" onPointerDown={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-line px-3 py-2.5">
          <span className="text-[13px] font-semibold text-fg-strong">{title}</span>
          <button type="button" onClick={onClose} aria-label="Close" className="ml-auto flex h-7 w-7 items-center justify-center rounded-lg text-fg-faint hover:bg-layer hover:text-fg"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-3.5">
          <SparkQLInput
            value={q}
            onChange={setQ}
            onSubmit={add}
            autoFocus
            placeholder="e.g. board:Alpha type:bug priority:high state:open"
          />
          <p className="mt-2 text-[11.5px] leading-relaxed text-fg-faint">
            Uses the same query language as the global search — combine <span className="font-mono text-fg-soft">board:</span> <span className="font-mono text-fg-soft">status:</span> <span className="font-mono text-fg-soft">assignee:me</span> <span className="font-mono text-fg-soft">state:open</span> and free text. Leave blank for all items.
          </p>
          <div className="mt-3 flex items-center justify-between">
            <span className="text-[12px] text-fg-faint">{loading ? 'Counting…' : count === null ? '' : `${count.toLocaleString()} match${count === 1 ? '' : 'es'}`}</span>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="rounded-lg px-3 py-1.5 text-[12.5px] text-fg-muted hover:bg-layer">Cancel</button>
              <button type="button" onClick={add} className="rounded-lg bg-accent px-3.5 py-1.5 text-[12.5px] font-semibold text-ink">{confirmLabel}</button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
